import React, { ChangeEvent, FC, useCallback, useEffect, useState } from 'react'
import styles from 'styles/components/template.module.scss'
import { SmallHeadline } from 'components/Typography'
import { Spacer } from 'components/Utility'
import { SearchForm, TagCheckbox } from 'components/Form'
import { StoreType } from 'types/store'
import { useDispatch, useSelector } from 'react-redux'
import { State } from 'index'
import { fetchStores, initStore } from 'reducks/store/operations'
import { filterStoresByKeyword, filterStoresByStockTypes } from 'lib/filter'
import { getStores } from 'reducks/store/selectors'
import { FlexRow } from 'components/Flex'
import { BlueButton, NavButton } from 'components/Button'
import { push } from 'connected-react-router'
import { CautionCard, StoreCardList } from 'components/Card'
import { initShop } from 'reducks/shop/operations'
import { initConfirm, updateEditState } from 'reducks/confirm/operations'
import { BREAD_STOCK_TYPES } from 'constants/bread'
import { StockUnionType } from 'types/stock'
import { FilterLabel } from 'components/Order'

const ShopsTemplate: FC = () => {
  const dispatch = useDispatch()

  const stores = useSelector((state: State) => getStores(state))

  const [keyword, setKeyword] = useState<string>('')
  const [storeList, setStoreList] = useState<StoreType[]>([])
  const [breadTypes, setBreadTypes] = useState<StockUnionType[]>([])

  // 検索キーワードの入力
  const handleKeyword = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value
      // 空白であれば初期値を設定
      if (value === '') {
        const filteredStoresByStockTypes = filterStoresByStockTypes(breadTypes, stores)
        setStoreList([...filteredStoresByStockTypes])
      }
      setKeyword(value)
    },
    [stores, setKeyword, setStoreList, breadTypes]
  )

  // キーワード検索(部分一致)
  const searchStores = useCallback(
    (keyword: string) => {
      const filteredStoresByKeyword = filterStoresByKeyword(keyword, stores)
      const filteredStoresByStockTypes = filterStoresByStockTypes(breadTypes, filteredStoresByKeyword)
      setStoreList([...filteredStoresByStockTypes])
    },
    [setStoreList, stores, breadTypes]
  )

  // 種別ごとの在庫有無
  const handleBreadTypes = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const id = event.target.id as StockUnionType
      setBreadTypes((prevState) => {
        const nextState = prevState.includes(id) ? [...prevState.filter((type) => type !== id)] : [...prevState, id]
        const filteredStoresByKeyword = filterStoresByKeyword(keyword, stores)
        const filteredStoresByStockTypes = filterStoresByStockTypes(nextState, filteredStoresByKeyword)
        setStoreList([...filteredStoresByStockTypes])
        return nextState
      })
      return
    },
    [setBreadTypes, keyword, stores]
  )

  const goToCreatePage = useCallback(() => {
    dispatch(push('/admin/shops/create/account'))
    dispatch(initConfirm())
    dispatch(initShop())
    dispatch(initStore())
  }, [dispatch])

  // 店舗リストに初期値を設定
  useEffect(() => {
    if (!stores) return
    setStoreList([...stores])
  }, [stores])

  // 店舗一覧を取得してRedux Storeに保存
  useEffect(() => {
    dispatch(fetchStores())
  }, [])

  // 店舗の編集中かどうかの値を初期化
  useEffect(() => {
    dispatch(updateEditState(false))
  }, [])

  return (
    <div className={styles.standard}>
      <Spacer size={28} />
      <SearchForm
        isFilled={keyword !== ''}
        onChange={handleKeyword}
        onClick={() => searchStores(keyword)}
        placeholder={'店舗名で検索する'}
        value={keyword}
      />
      <Spacer size={28} />
      <FilterLabel label={'在庫ステータス：'} />
      <Spacer size={8} />
      <FlexRow justifyContent={'start'} wrap={'wrap'}>
        {BREAD_STOCK_TYPES.map((BREAD_STOCK_TYPE) => {
          const { id, name } = BREAD_STOCK_TYPE
          return (
            <TagCheckbox
              key={id}
              hasHashtag={false}
              id={id}
              isChecked={breadTypes.some((type) => type === id)}
              onChange={handleBreadTypes}
              type={'outlined'}
              value={name}
            />
          )
        })}
      </FlexRow>
      <Spacer size={28} />
      <FlexRow justifyContent={'between'}>
        <SmallHeadline label={`${storeList.length}件の店舗を表示中`} />
        <BlueButton
          label={'新しく登録する'}
          onClick={goToCreatePage}
          size={'xsmall'}
          startIconPath={'/icon/white/add.svg'}
          type={'main'}
        />
      </FlexRow>
      <Spacer size={8} />
      {storeList.length > 0 ? (
        <StoreCardList stores={storeList} />
      ) : (
        <CautionCard
          align={'center'}
          caution={'該当店舗がありません'}
          description={'別の言葉で検索してみてください。'}
        />
      )}
      <Spacer size={12} />
      <FlexRow justifyContent={'end'}>
        <BlueButton
          label={'新しく登録する'}
          onClick={goToCreatePage}
          size={'xsmall'}
          startIconPath={'/icon/white/add.svg'}
          type={'main'}
        />
      </FlexRow>
      <Spacer size={48} />
      <NavButton label={'トップページに戻る'} iconPath={'/icon/grey/home.svg'} url={'/admin/shops'} />
      <Spacer size={72} />
    </div>
  )
}

export default ShopsTemplate
